// Phase 0.1 (grade-2 ceiling) — BANDS AS DATA.
//
// Single ordered source of truth for the engine's RANGE-vocabulary age
// bands ("4.0-4.5" … "7.5+"). Before this, the AgeBand union was
// hand-duplicated in FOUR files (adaptationTypes.ts, @/types/engine,
// @/types/progression, and parentPriors' AGE_BANDS array) and every
// per-band number lived in its own exhaustive switch (sessionLengthForAge,
// blockSizeForAge, readingLevelForAgeBand, AGE_BAND_TO_GRADE). Adding a
// grade was a five-switch surgery with a silent-fall-through trap (a new
// length with no interleave case dropped to all-focal). Now: a new band is
// ONE ROW here, the AgeBand type is DERIVED from the table, and the
// consumers READ the table.
//
// SCOPE NOTE (conservative activation): the new fine bands "7.0-7.5" /
// "7.5+" are added here so they are type-valid and fully configured (no
// silent fall-through). The runtime age→band resolver (parentPriors'
// bandContaining / placementEngine) is intentionally NOT yet changed to
// EMIT them — that activation is paired with authoring the grade-2 skills
// that live in those bands (Phase 1+). Existing bands keep their exact
// ranks (0-6), so no placement/telemetry behavior changes today.
//
// MA-343 (ADR-099): the `previewWindow` column + its `PREVIEW_WINDOW_
// FULL_CHAPTER` sentinel — feeding the retired `previewWindowForAge`
// (progression.ts), itself feeding only the deleted PATH_SPEC demo layer
// — are removed. Verified zero surviving consumers before deleting.

export type ReadingLevel = "A" | "B" | "C" | "D";

export interface AgeBandRow {
  /** The AgeBand literal (also the curriculum-order key, by array index). */
  label: string;
  /** US K-12 grade-band label for parent-facing surfaces. */
  gradeBand: string;
  /** Items per session for this band (sessionLengthForAge). */
  sessionLength: number;
  /** In-session chunk-checkpoint block size (blockSizeForAge). */
  blockSize: number;
  /** Reading capacity gate for book unlocks (readingLevelForAgeBand). */
  readingLevel: ReadingLevel;
}

// Ordered ASCENDING by curriculum rank. Existing rows 0-6 are byte-for-byte
// the legacy values (do not reorder — parentPriors.bandRank depends on the
// index). New grade-2 rows are APPENDED so existing ranks are preserved.
export const AGE_BAND_TABLE = [
  { label: "4.0-4.5", gradeBand: "Pre-K", sessionLength: 8,  blockSize: 4, readingLevel: "A" },
  { label: "4.5-5.0", gradeBand: "Pre-K", sessionLength: 9,  blockSize: 4, readingLevel: "A" },
  { label: "5.0-5.5", gradeBand: "K",     sessionLength: 12, blockSize: 4, readingLevel: "B" },
  { label: "5.5-6.0", gradeBand: "K",     sessionLength: 12, blockSize: 5, readingLevel: "C" },
  { label: "6.0-6.5", gradeBand: "1",     sessionLength: 15, blockSize: 5, readingLevel: "D" },
  { label: "6.5-7.0", gradeBand: "1",     sessionLength: 18, blockSize: 6, readingLevel: "D" },
  { label: "7.0+",    gradeBand: "2",     sessionLength: 20, blockSize: 6, readingLevel: "D" },
  // ── Grade-2 ceiling: new fine bands (dormant until skills are authored) ──
  { label: "7.0-7.5", gradeBand: "2",     sessionLength: 20, blockSize: 6, readingLevel: "D" },
  { label: "7.5+",    gradeBand: "3",     sessionLength: 20, blockSize: 6, readingLevel: "D" },
  // ── Grade-3 slice: first grade-3 band. CONFIGURED + type-valid; the
  //    age->band resolver (parentPriors.bandContaining) still emits "7.5+"
  //    for 8.x ages because the open-ended "7.5+" row precedes this one and
  //    matches first (resolver-dormant, exactly how the grade-2 fine bands
  //    were first added — see SCOPE NOTE above). The grade-3 skills banded
  //    here (ms.math.26/27/28) become reachable via the chapter walk /
  //    placement once the grade-2 ceiling is mastered. sessionLength 20 +
  //    blockSize 6 reuse the existing interleave (case 20) + block cases, so
  //    no progression.ts case is needed.
  { label: "8.0-8.5", gradeBand: "3",     sessionLength: 20, blockSize: 6, readingLevel: "D" },
] as const satisfies readonly AgeBandRow[];

/** The AgeBand union, DERIVED from the table — the single source of truth. */
export type AgeBand = (typeof AGE_BAND_TABLE)[number]["label"];

/** Age bands in ascending curriculum-rank order (the labels of the table). */
export const AGE_BAND_ORDER: readonly AgeBand[] = AGE_BAND_TABLE.map((r) => r.label);

const BY_LABEL = Object.fromEntries(
  AGE_BAND_TABLE.map((r) => [r.label, r]),
) as Record<AgeBand, AgeBandRow>;

/** The full config row for a band. */
export function ageBandRow(band: AgeBand): AgeBandRow {
  return BY_LABEL[band];
}
